import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
} from "react";
import { useMutation, useQuery } from "convex/react";
import { permissionsForRole } from "@papersflow/auth";
import {
  decodeAppContext,
  decodeConnectionSummary,
  type AppContext,
  type ConnectionSummary,
  type ProjectAccess,
} from "@papersflow/contracts";
import { api } from "../../../backend/convex/_generated/api";
import type { Doc, Id } from "../../../backend/convex/_generated/dataModel";
import { getReadyWorkspaceAuthClient } from "../providers/BackendProviders";
import { projectWorkspaceConnectionAccess } from "./workspaceConnectionAccess";
import {
  WorkspaceContext,
  type ActiveViewProjection,
  type ActiveViewProjectionAuthority,
  type ActiveViewProjectionPublication,
  type WorkspaceContextValue,
  type WorkspaceRouteScope,
  type WorkspaceThread,
  reconcileActiveViewProjection,
} from "./WorkspaceContext";
import { authorizedSelectedRecord } from "./workspaceSelectionModel";

function workspaceDataIsLoading({
  workspaceLoaded,
  hasWorkspace,
  canReadProjects,
  projectsLoaded,
  artifactsLoaded,
  canReadThreads,
  threadsLoaded,
  canUseConnections,
  connectionsLoaded,
}: {
  readonly workspaceLoaded: boolean;
  readonly hasWorkspace: boolean;
  readonly canReadProjects: boolean;
  readonly projectsLoaded: boolean;
  readonly artifactsLoaded: boolean;
  readonly canReadThreads: boolean;
  readonly threadsLoaded: boolean;
  readonly canUseConnections: boolean;
  readonly connectionsLoaded: boolean;
}): boolean {
  if (!workspaceLoaded) return true;
  if (!hasWorkspace) return false;
  if (canReadProjects && (!projectsLoaded || !artifactsLoaded)) return true;
  if (canReadThreads && !threadsLoaded) return true;
  return canUseConnections && !connectionsLoaded;
}

function useConnectionAccessProjection({
  workspaceRowsLoaded,
  activeWorkspace,
  canUseConnections,
  connectionsLoaded,
  connectedServices,
}: {
  readonly workspaceRowsLoaded: boolean;
  readonly activeWorkspace: Doc<"workspaces"> | undefined;
  readonly canUseConnections: boolean;
  readonly connectionsLoaded: boolean;
  readonly connectedServices: readonly ConnectionSummary[];
}) {
  return useMemo(
    () =>
      projectWorkspaceConnectionAccess({
        workspaceRowsLoaded,
        workspaceName: activeWorkspace?.name,
        canUseConnections,
        connectionsLoaded,
        connections: connectedServices,
      }),
    [
      activeWorkspace?.name,
      canUseConnections,
      connectedServices,
      connectionsLoaded,
      workspaceRowsLoaded,
    ],
  );
}

function projectTaskWriteAvailable(
  hasPermission: boolean,
  access: ProjectAccess | undefined,
): boolean {
  return hasPermission && (access === "owner" || access === "member");
}

function projectQueryArgs(
  activeWorkspace: Doc<"workspaces"> | undefined,
  canReadProjects: boolean,
) {
  if (!activeWorkspace || !canReadProjects) return "skip" as const;
  return { workspaceId: activeWorkspace._id };
}

function useWorkspaceRecords({
  selectedWorkspaceId,
  selectedProjectId,
  selectedThreadId,
}: {
  readonly selectedWorkspaceId: Id<"workspaces"> | undefined;
  readonly selectedProjectId: Id<"projects"> | undefined;
  readonly selectedThreadId: Id<"threads"> | undefined;
}) {
  const workspaceRows = useQuery(api.workspaces.listMine);
  const workspaces = useMemo(
    () =>
      (workspaceRows ?? []).flatMap((entry) =>
        entry.workspace ? [{ membership: entry.membership, workspace: entry.workspace }] : [],
      ),
    [workspaceRows],
  );
  const activeWorkspaceEntry = authorizedSelectedRecord(
    workspaces,
    selectedWorkspaceId,
    (entry) => entry.workspace._id,
  );
  const activeWorkspace = activeWorkspaceEntry?.workspace;
  const activeMembership = activeWorkspaceEntry?.membership;
  const permissions = activeMembership ? permissionsForRole(activeMembership.role) : [];
  const canReadProjects = permissions.includes("project.read");
  const canReadThreads = permissions.includes("thread.read");
  const canUseConnections = permissions.includes("connection.use");
  const canManageProjects = permissions.includes("project.manage");
  const canWriteThreads = permissions.includes("thread.write");
  const hasTaskWritePermission = permissions.includes("task.write");
  const projectQuery = projectQueryArgs(activeWorkspace, canReadProjects);
  const projectRows = useQuery(api.projects.list, projectQuery);
  const projectCatalogRows = useQuery(api.projects.catalog, projectQuery);
  const projects = useMemo(() => projectRows ?? [], [projectRows]);
  const activeProject = authorizedSelectedRecord(
    projects,
    selectedProjectId,
    (project) => project._id,
  );
  const activeProjectAccess = projectCatalogRows?.find(
    (item) => item.ref.projectId === activeProject?._id,
  )?.access;
  const canWriteTasks = projectTaskWriteAvailable(hasTaskWritePermission, activeProjectAccess);
  const threadRows = useQuery(
    api.threads.list,
    activeWorkspace && canReadThreads ? { workspaceId: activeWorkspace._id } : "skip",
  );
  const connectionRows = useQuery(
    api.connections.list,
    activeWorkspace && canUseConnections ? { workspaceId: activeWorkspace._id } : "skip",
  );
  const artifactRows = useQuery(
    api.artifacts.list,
    activeWorkspace && activeProject && canReadProjects
      ? {
          workspaceId: activeWorkspace._id,
          projectId: activeProject._id,
          limit: 100,
        }
      : "skip",
  );
  const activeProjectIds = useMemo(
    () => new Set(projects.map((project) => project._id)),
    [projects],
  );
  const threads = useMemo<readonly WorkspaceThread[]>(
    () =>
      (threadRows ?? []).filter(
        (thread) => thread.archivedAt === undefined && activeProjectIds.has(thread.projectId),
      ),
    [activeProjectIds, threadRows],
  );
  const artifacts = useMemo(() => artifactRows ?? [], [artifactRows]);
  const activeProjectId = activeProject?._id;
  const projectThreads = threads.filter((thread) => thread.projectId === activeProjectId);
  const activeThread = authorizedSelectedRecord(
    projectThreads,
    selectedThreadId,
    (thread) => thread._id,
  );
  const connectedServices = useMemo(
    () =>
      (connectionRows ?? []).map((connection) =>
        decodeConnectionSummary({
          provider: connection.toolkit,
          scope: connection.scope,
          status: connection.status,
          ...(connection.displayName !== undefined ? { displayName: connection.displayName } : {}),
          capabilities: connection.capabilities,
        }),
      ),
    [connectionRows],
  );
  const connectionAccess = useConnectionAccessProjection({
    workspaceRowsLoaded: workspaceRows !== undefined,
    activeWorkspace,
    canUseConnections,
    connectionsLoaded: connectionRows !== undefined,
    connectedServices,
  });
  const dataLoading = workspaceDataIsLoading({
    workspaceLoaded: workspaceRows !== undefined,
    hasWorkspace: activeWorkspace !== undefined,
    canReadProjects,
    projectsLoaded: projectRows !== undefined,
    artifactsLoaded: activeProject === undefined || artifactRows !== undefined,
    canReadThreads,
    threadsLoaded: threadRows !== undefined,
    canUseConnections,
    connectionsLoaded: connectionRows !== undefined,
  });

  return {
    activeWorkspace,
    activeMembership,
    activeProject,
    activeThread,
    projects,
    threads,
    artifacts,
    connectedServices,
    connectionAccess,
    permissions,
    dataLoading,
    canManageProjects,
    canWriteThreads,
    canWriteTasks,
  };
}

function useWorkspaceCommands({
  activeWorkspace,
  threads,
  setSelectedWorkspaceId,
  setSelectedProjectId,
  setSelectedThreadId,
  clearActiveViewProjection,
}: {
  readonly activeWorkspace: Doc<"workspaces"> | undefined;
  readonly threads: readonly WorkspaceThread[];
  readonly setSelectedWorkspaceId: Dispatch<SetStateAction<Id<"workspaces"> | undefined>>;
  readonly setSelectedProjectId: Dispatch<SetStateAction<Id<"projects"> | undefined>>;
  readonly setSelectedThreadId: Dispatch<SetStateAction<Id<"threads"> | undefined>>;
  readonly clearActiveViewProjection: () => void;
}) {
  const ensurePersonal = useMutation(api.workspaces.ensurePersonal);
  const insertProject = useMutation(api.projects.create);
  const insertThread = useMutation(api.threads.create);
  const renameThreadMutation = useMutation(api.threads.rename);
  const setThreadPinnedMutation = useMutation(api.threads.setPinned);

  const selectRouteScope = useCallback(
    (scope: WorkspaceRouteScope) => {
      clearActiveViewProjection();
      setSelectedWorkspaceId(scope.workspaceId);
      setSelectedProjectId(scope.projectId);
      setSelectedThreadId(scope.threadId);
    },
    [clearActiveViewProjection, setSelectedProjectId, setSelectedThreadId, setSelectedWorkspaceId],
  );

  const selectProject = useCallback(
    (projectId: Id<"projects">) => {
      clearActiveViewProjection();
      setSelectedProjectId(projectId);
      setSelectedThreadId(undefined);
    },
    [clearActiveViewProjection, setSelectedProjectId, setSelectedThreadId],
  );
  const selectThread = useCallback(
    (threadId: Id<"threads">) => {
      clearActiveViewProjection();
      const thread = threads.find((candidate) => candidate._id === threadId);
      if (thread?.projectId) setSelectedProjectId(thread.projectId);
      setSelectedThreadId(threadId);
    },
    [clearActiveViewProjection, setSelectedProjectId, setSelectedThreadId, threads],
  );
  const ensurePersonalWorkspace = useCallback(async () => {
    const workspaceId = await ensurePersonal({});
    clearActiveViewProjection();
    setSelectedWorkspaceId(workspaceId);
  }, [clearActiveViewProjection, ensurePersonal, setSelectedWorkspaceId]);
  const createProject = useCallback(
    async (name: string, description?: string, instructions?: string) => {
      if (!activeWorkspace) throw new Error("Create a workspace before adding a project");
      const trimmedDescription = description?.trim();
      const trimmedInstructions = instructions?.trim();
      const result = await insertProject({
        requestId: crypto.randomUUID(),
        workspaceId: activeWorkspace._id,
        name: name.trim(),
        ...(trimmedDescription ? { description: trimmedDescription } : {}),
        ...(trimmedInstructions ? { instructions: trimmedInstructions } : {}),
        visibility: "restricted",
        initialMembers: [],
      });
      const projectId = result.ref.projectId;
      clearActiveViewProjection();
      setSelectedProjectId(projectId);
      setSelectedThreadId(undefined);
      return projectId;
    },
    [
      activeWorkspace,
      insertProject,
      clearActiveViewProjection,
      setSelectedProjectId,
      setSelectedThreadId,
    ],
  );
  const createThread = useCallback(
    async (projectId: Id<"projects">, title?: string) => {
      if (!activeWorkspace) throw new Error("Create a workspace before adding a thread");
      const trimmedTitle = title?.trim();
      const threadId = await insertThread({
        workspaceId: activeWorkspace._id,
        projectId,
        surface: "projects",
        title: trimmedTitle || "New thread",
        titleOrigin: trimmedTitle ? "manual" : "automatic",
      });
      clearActiveViewProjection();
      setSelectedProjectId(projectId);
      setSelectedThreadId(threadId);
      return threadId;
    },
    [
      activeWorkspace,
      insertThread,
      clearActiveViewProjection,
      setSelectedProjectId,
      setSelectedThreadId,
    ],
  );
  const renameThread = useCallback(
    async (threadId: Id<"threads">, title: string) => {
      if (!activeWorkspace) throw new Error("Select a workspace before renaming a thread");
      await renameThreadMutation({ workspaceId: activeWorkspace._id, threadId, title });
    },
    [activeWorkspace, renameThreadMutation],
  );
  const setThreadPinned = useCallback(
    async (threadId: Id<"threads">, pinned: boolean) => {
      if (!activeWorkspace) throw new Error("Select a workspace before pinning a thread");
      await setThreadPinnedMutation({ workspaceId: activeWorkspace._id, threadId, pinned });
    },
    [activeWorkspace, setThreadPinnedMutation],
  );

  return {
    selectRouteScope,
    selectProject,
    selectThread,
    ensurePersonalWorkspace,
    createProject,
    createThread,
    renameThread,
    setThreadPinned,
  } as const;
}

function useProjectionHasCurrentAuthority({
  workspaceId,
  projectId,
  threadId,
}: {
  readonly workspaceId: Id<"workspaces"> | undefined;
  readonly projectId: Id<"projects"> | undefined;
  readonly threadId: Id<"threads"> | undefined;
}) {
  return useCallback(
    (projection: ActiveViewProjection): boolean =>
      projection.workspaceId === workspaceId &&
      projection.projectId === projectId &&
      projection.threadId === (threadId ?? null),
    [projectId, threadId, workspaceId],
  );
}

function useCreateWorkspaceSnapshot({
  activeWorkspace,
  activeMembership,
  activeProject,
  activeThread,
  activeViewProjection,
  projectionHasCurrentAuthority,
  permissions,
  connectedServices,
}: {
  readonly activeWorkspace: Doc<"workspaces"> | undefined;
  readonly activeMembership: Doc<"memberships"> | undefined;
  readonly activeProject: Doc<"projects"> | undefined;
  readonly activeThread: WorkspaceThread | undefined;
  readonly activeViewProjection: ActiveViewProjection | null;
  readonly projectionHasCurrentAuthority: (projection: ActiveViewProjection) => boolean;
  readonly permissions: AppContext["actor"]["permissions"];
  readonly connectedServices: readonly ConnectionSummary[];
}): WorkspaceContextValue["createSnapshot"] {
  return useCallback(
    (overrides: Partial<AppContext> = {}): AppContext => {
      if (!activeWorkspace || !activeMembership) {
        throw new Error("An authenticated workspace is required");
      }
      const currentView =
        activeViewProjection && projectionHasCurrentAuthority(activeViewProjection)
          ? activeViewProjection
          : null;
      return decodeAppContext({
        version: 1,
        requestId: crypto.randomUUID(),
        surface: "projects",
        route: window.location.pathname,
        workspaceId: activeWorkspace._id,
        ...(activeProject ? { projectId: activeProject._id } : {}),
        ...(activeThread ? { threadId: activeThread._id } : {}),
        ...(currentView
          ? {
              viewer: currentView.viewer,
              ...(currentView.runtime ? { runtime: currentView.runtime } : {}),
              ...(currentView.artifact ? { artifact: currentView.artifact } : {}),
              ...(currentView.selection ? { selection: currentView.selection } : {}),
              ...(currentView.openNote ? { openNote: currentView.openNote } : {}),
            }
          : {}),
        actor: {
          userId: activeMembership.userId,
          workspaceId: activeWorkspace._id,
          role: activeMembership.role,
          permissions: [...permissions],
        },
        connections: connectedServices,
        clientCapabilities: [
          "web",
          "streaming-ui",
          "agent-activity-events",
          "subagent-streaming",
          "approval-cards",
        ],
        locale: navigator.language || "en",
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
        capturedAt: new Date().toISOString(),
        ...overrides,
      });
    },
    [
      activeMembership,
      activeProject,
      activeThread,
      activeViewProjection,
      activeWorkspace,
      connectedServices,
      permissions,
      projectionHasCurrentAuthority,
    ],
  );
}

export function WorkspaceContextProvider({ children }: PropsWithChildren) {
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<Id<"workspaces">>();
  const [selectedProjectId, setSelectedProjectId] = useState<Id<"projects">>();
  const [selectedThreadId, setSelectedThreadId] = useState<Id<"threads">>();
  const [activeViewPublication, setActiveViewPublication] =
    useState<ActiveViewProjectionPublication | null>(null);
  const clearActiveViewProjection = useCallback(() => setActiveViewPublication(null), []);
  const setActiveViewProjection = useCallback(
    (authority: ActiveViewProjectionAuthority, projection: ActiveViewProjection | null) => {
      setActiveViewPublication((current) =>
        reconcileActiveViewProjection(current, authority, projection),
      );
    },
    [],
  );
  const activeViewProjection = activeViewPublication?.projection ?? null;
  const {
    activeWorkspace,
    activeMembership,
    activeProject,
    activeThread,
    projects,
    threads,
    artifacts,
    connectedServices,
    connectionAccess,
    permissions,
    dataLoading,
    canManageProjects,
    canWriteThreads,
    canWriteTasks,
  } = useWorkspaceRecords({ selectedWorkspaceId, selectedProjectId, selectedThreadId });
  const {
    selectRouteScope,
    selectProject,
    selectThread,
    ensurePersonalWorkspace,
    createProject,
    createThread,
    renameThread,
    setThreadPinned,
  } = useWorkspaceCommands({
    activeWorkspace,
    threads,
    setSelectedWorkspaceId,
    setSelectedProjectId,
    setSelectedThreadId,
    clearActiveViewProjection,
  });

  const projectionHasCurrentAuthority = useProjectionHasCurrentAuthority({
    workspaceId: activeWorkspace?._id,
    projectId: activeProject?._id,
    threadId: activeThread?._id,
  });
  const createSnapshot = useCreateWorkspaceSnapshot({
    activeWorkspace,
    activeMembership,
    activeProject,
    activeThread,
    activeViewProjection,
    projectionHasCurrentAuthority,
    permissions,
    connectedServices,
  });

  const rawCurrentUser = useQuery(api.auth.getCurrentUser);

  const currentUser = useMemo(() => {
    if (!rawCurrentUser) return null;
    return {
      id: rawCurrentUser._id,
      name: rawCurrentUser.name ?? null,
      email: rawCurrentUser.email ?? null,
      image: rawCurrentUser.image ?? null,
    };
  }, [rawCurrentUser]);

  const signOut = useCallback(async () => {
    const authClient = getReadyWorkspaceAuthClient();
    await authClient?.signOut?.();
    window.location.reload();
  }, []);

  const value = useMemo<WorkspaceContextValue>(
    () => ({
      workspaceName: activeWorkspace?.name ?? "No workspace",
      projectName: activeProject?.name ?? "No project",
      activeWorkspace,
      activeMembership,
      activeProject,
      activeThread,
      projects,
      threads,
      artifacts,
      connectedServices,
      connectionAccess,
      dataLoading,
      canManageProjects,
      canWriteThreads,
      canWriteTasks,
      currentUser,
      signOut,
      selectRouteScope,
      selectProject,
      selectThread,
      ensurePersonalWorkspace,
      createProject,
      createThread,
      renameThread,
      setThreadPinned,
      setActiveViewProjection,
      createSnapshot,
    }),
    [
      activeMembership,
      activeProject,
      activeThread,
      activeWorkspace,
      artifacts,
      canManageProjects,
      canWriteThreads,
      canWriteTasks,
      connectionAccess,
      connectedServices,
      createProject,
      createSnapshot,
      createThread,
      currentUser,
      dataLoading,
      ensurePersonalWorkspace,
      projects,
      renameThread,
      selectRouteScope,
      selectProject,
      selectThread,
      setActiveViewProjection,
      setThreadPinned,
      signOut,
      threads,
    ],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}
